import ClearIcon from '@mui/icons-material/Clear'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiError, api, type AuditEvent } from '../api/client'
import { exactTime } from '../lib/time'

/**
 * One-click starting points.
 *
 * The log is mostly routine sign-ins, and the rows worth finding are the ones
 * that cost money or change who can spend it. Actions not present on the server
 * are filtered out before use, so a preset cannot quietly select nothing.
 */
const PRESETS: { label: string; actions: string[]; hint: string }[] = [
  {
    label: 'Money',
    actions: ['gpu_power_on', 'gpu_power_off', 'job_submitted'],
    hint: 'GPU switched on or off, and covers ordered',
  },
  {
    label: 'Access',
    actions: ['user_created', 'user_updated', 'user_deleted', 'password_changed'],
    hint: 'Accounts added, changed or removed',
  },
  {
    label: 'Sign-in problems',
    actions: ['login_failed', 'login_throttled'],
    hint: 'Wrong passwords and rate-limited attempts',
  },
]

/** login_failed -> "Login failed". The server's vocabulary is snake_case. */
function label(action: string): string {
  const words = action.replaceAll('_', ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** Sign-in trouble is the thing you scan for, so it gets the only colour. */
function tone(action: string): 'default' | 'warning' | 'error' {
  if (action === 'login_throttled') return 'error'
  if (action === 'login_failed') return 'warning'
  return 'default'
}

/**
 * A calendar date from the picker, as the instants that day begins and ends.
 *
 * `new Date('2026-08-07T00:00:00')` -- a datetime with no offset -- is parsed as
 * local time, which is what the reader meant; toISOString then states that
 * instant in UTC so the server cannot misread it. (A bare '2026-08-07' would be
 * parsed as UTC instead, which is the trap this avoids.)
 */
function dayBounds(date: string): { start: string; end: string } {
  return {
    start: new Date(`${date}T00:00:00`).toISOString(),
    end: new Date(`${date}T23:59:59.999`).toISOString(),
  }
}

const PAGE_SIZES = [25, 50, 100, 250]

export default function AuditPage() {
  const [events, setEvents] = useState<AuditEvent[] | null>(null)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [vocabulary, setVocabulary] = useState<string[]>([])

  const [actions, setActions] = useState<string[]>([])
  const [username, setUsername] = useState('')
  const [since, setSince] = useState('')
  const [until, setUntil] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)

  // What the field shows updates on every keystroke; what gets queried waits
  // until typing stops. Without this, "producer" is eight requests, and the
  // seven wasted ones each run a COUNT over the table.
  const [typedUsername, setTypedUsername] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setUsername(typedUsername), 300)
    return () => clearTimeout(timer)
  }, [typedUsername])

  // Only the newest request may paint. Filters change faster than requests
  // return, and a slow earlier one landing last would show rows that do not
  // match what the form says.
  const latest = useRef(0)

  useEffect(() => {
    api.listAuditActions().then(setVocabulary, () => setVocabulary([]))
  }, [])

  const load = useCallback(async () => {
    const ticket = latest.current + 1
    latest.current = ticket
    setEvents(null)
    try {
      const result = await api.listAudit({
        actions,
        username: username.trim() || undefined,
        // A date alone means the whole of that day, in the reader's own zone.
        // Both halves matter. Sending a bare date as `until` would exclude
        // everything after 00:00:00 on the day they asked to include -- and
        // sending a naive local string would be read as UTC, so in Finland the
        // first three hours of the chosen day would vanish while three hours of
        // the following day quietly appeared. dayBounds resolves the local
        // instant and sends it with an offset.
        since: since ? dayBounds(since).start : undefined,
        until: until ? dayBounds(until).end : undefined,
        limit: pageSize,
        offset: page * pageSize,
      })
      if (ticket !== latest.current) return
      setEvents(result.items)
      setTotal(result.total)
      setError(null)
    } catch (err) {
      if (ticket !== latest.current) return
      setEvents([])
      setError(err instanceof ApiError ? err.message : 'Could not load the audit log.')
    }
  }, [actions, username, since, until, page, pageSize])

  useEffect(() => {
    void load()
  }, [load])

  /** Any filter change puts the reader back on the first page: staying on page
   *  4 of a narrower result set shows an empty table that looks like no matches. */
  function change(apply: () => void) {
    apply()
    setPage(0)
  }

  const filtered = actions.length > 0 || typedUsername.trim() !== '' || since !== '' || until !== ''

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5" component="h2">
          Audit Log
        </Typography>
        <Tooltip title="Reload">
          <IconButton onClick={() => void load()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Show:
            </Typography>
            {PRESETS.map((preset) => {
              const usable = preset.actions.filter((action) => vocabulary.includes(action))
              const active =
                usable.length > 0 &&
                usable.every((action) => actions.includes(action)) &&
                actions.length === usable.length
              return (
                <Tooltip key={preset.label} title={preset.hint}>
                  <Chip
                    label={preset.label}
                    size="small"
                    color={active ? 'primary' : 'default'}
                    variant={active ? 'filled' : 'outlined'}
                    disabled={usable.length === 0}
                    onClick={() => change(() => setActions(active ? [] : usable))}
                  />
                </Tooltip>
              )
            })}
            {filtered && (
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={() =>
                  change(() => {
                    setActions([])
                    // Both: the debounced value is what gets queried, so
                    // clearing only the field would leave the previous name
                    // filtering the table for 300ms while the screen claimed
                    // nothing was filtered.
                    setTypedUsername('')
                    setUsername('')
                    setSince('')
                    setUntil('')
                  })
                }
              >
                Clear
              </Button>
            )}
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              select
              size="small"
              label="Action"
              value={actions}
              onChange={(event) => {
                const value = event.target.value
                change(() => setActions(typeof value === 'string' ? value.split(',') : value))
              }}
              SelectProps={{
                multiple: true,
                renderValue: (selected) =>
                  (selected as string[]).length === 0
                    ? 'Any'
                    : (selected as string[]).map(label).join(', '),
              }}
              sx={{ minWidth: 240, flexGrow: 1 }}
            >
              {vocabulary.map((action) => (
                <MenuItem key={action} value={action}>
                  {label(action)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label="User"
              placeholder="Any"
              value={typedUsername}
              onChange={(event) => change(() => setTypedUsername(event.target.value))}
              helperText="Exact name, including accounts since deleted"
              sx={{ minWidth: 200 }}
            />
            <TextField
              size="small"
              type="date"
              label="From"
              value={since}
              onChange={(event) => change(() => setSince(event.target.value))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              value={until}
              onChange={(event) => change(() => setUntil(event.target.value))}
              InputLabelProps={{ shrink: true }}
              helperText="Includes the whole day"
            />
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}

      <Paper>
        {events === null ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : events.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {filtered ? 'Nothing matches those filters.' : 'Nothing recorded yet.'}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>Detail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.map((event) => (
                    <TableRow key={event.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" color="text.secondary">
                          {exactTime(event.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={tone(event.action)}
                          label={label(event.action)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {event.username ?? '—'}
                          {event.username && event.user_id === null && (
                            <Tooltip title="This account no longer exists">
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 0.5 }}
                              >
                                (deleted)
                              </Typography>
                            </Tooltip>
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {event.ip ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {event.detail ?? ''}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_event, next) => setPage(next)}
              rowsPerPage={pageSize}
              rowsPerPageOptions={PAGE_SIZES}
              onRowsPerPageChange={(event) => change(() => setPageSize(Number(event.target.value)))}
            />
          </>
        )}
      </Paper>
    </Stack>
  )
}
